import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, MessageSquare, Users, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function DevelopmentPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-green-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-green-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-500">Programs</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Development</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200 mb-6">
              <BookOpen className="w-4 h-4 text-brand-green" />
              <span className="text-sm font-medium text-green-700">Development Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Empowering{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-green-600">
                Continuous Development
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              At DC Men, we are committed to providing our members with the knowledge, skills and dispositions necessary for success in the field of early childhood education.
            </p>
          </div>
        </div>
      </section>

      {/* Our Development Focus */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">Our Development Focus</h2>
              <div className="space-y-4 text-navy-600 leading-relaxed">
                <p>
                  At DC Men, we are committed to providing our members with the knowledge, skills and dispositions necessary for success in the field of early childhood education.
                </p>
                <p>
                  By promoting nurturing, ongoing communication, collegial dialogue and a range of professional development opportunities we intend to create a learning environment that not only encourages growth but also empowers men to reach their full potential.
                </p>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-green-50 to-gold-50 rounded-3xl p-4 shadow-xl overflow-hidden">
                <img 
                  src="/images/event-photo-2.jpg" 
                  alt="DC Men in ECE Development Program"
                  className="w-full h-72 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Pillars */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">How We Develop Leaders</h2>
            <p className="text-navy-300">Our comprehensive approach to professional growth and development.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-green/30 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center mb-6 shadow-lg shadow-green-500/20">
                  <MessageSquare className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Ongoing Communication</h3>
                <p className="text-navy-300 leading-relaxed">We prioritize continuous interaction among our community members, facilitating a flow of ideas, feedback, and knowledge that strengthens our collective mission. Regular updates and structured touchpoints ensure that everyone is aligned and informed.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-gold/30 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center mb-6 shadow-lg shadow-gold-500/20">
                  <Users className="w-7 h-7 text-navy-900" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Collegial Dialogue</h3>
                <p className="text-navy-300 leading-relaxed">Engage in meaningful discussions that are foundational to personal and professional growth. Our forums and meetings are spaces where experiences are shared, challenges are discussed, and solutions are brainstormed together.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-red/30 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Supportive Environments</h3>
                <p className="text-navy-300 leading-relaxed">DC Men believes in building spaces that are conducive to growth. Whether it's through mentorship programs or collaborative projects, we ensure that our environments inspire and motivate our members to excel.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Get Involved!
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Join DC Men and be part of a movement dedicated to empowering young men through education, development, and community engagement. Together, we can make a significant impact in the lives of men of color in the DMV area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-green hover:bg-green-600 text-white font-semibold px-8 shadow-xl shadow-green-500/20">
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/retention">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50">
                Explore Retention
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
